import {
  useGetPricingQuery,
  getPricingQueryKey,
  getPricingQueryOptions,
} from '@repo/studio-hooks-coins/gen/service/creativefabrica/coins/v2'
import type { PricingAction } from '@repo/studio-hooks-coins/gen/service/creativefabrica/coins/v2/types_pb'

// Pricing-query helpers consumers need to read/prefetch the cached table by its
// canonical key (e.g. lumo's board loader prefetch + BusinessLogicStore cache
// read). Surfaced here, alongside usePriceQuery, so callers go through this
// module rather than reaching into the gen/ folder across the package boundary.
// The PricingAction enum is surfaced separately via @repo/studio-hooks-coins/enums.
// oxlint-disable-next-line cf-web/no-barrel-file
export { getPricingQueryKey, getPricingQueryOptions }
// oxlint-disable-next-line cf-web/no-barrel-file
export type { GetPricingResponse } from '@repo/studio-hooks-coins/gen/service/creativefabrica/coins/v2/service_pb'

// GetPricing returns the full price table when called with an empty request.
// Every consumer passes the same `{}` variables, so react-query dedupes and
// caches a single network request and each hook selects its own action.
/** Variables every price query/lookup must share so react-query caches one entry. */
export const PRICING_QUERY_VARIABLES = {}

const ONE_HOUR_MS = 60 * 60 * 1000

// Prices change rarely; avoid refetching the whole table on every mount/focus.
const PRICING_QUERY_OPTIONS = {
  staleTime: ONE_HOUR_MS,
  refetchOnMount: false,
  refetchOnWindowFocus: false,
} as const

/**
 * Coin price for a single {@link PricingAction}, sourced from the coins.v2
 * `GetPricing` RPC. Replaces the legacy GraphQL `coinActionFees` lookup
 * (`useCoinActionFeeQuery`).
 *
 * Returns `undefined` until the price table resolves or when the action has no
 * entry. `amount` is an int64 (bigint) on the wire; coin prices are well within
 * the safe-integer range, so it is narrowed to `number` for callers.
 *
 * Pass `{ enabled: false }` to defer the fetch (e.g. when the action is only
 * conditionally relevant).
 */
export function usePriceQuery(action: PricingAction, options?: { enabled?: boolean }) {
  return useGetPricingQuery(PRICING_QUERY_VARIABLES, {
    ...PRICING_QUERY_OPTIONS,
    enabled: options?.enabled,
    select: data => {
      const amount = data.items.find(item => item.action === action)?.amount
      return amount === undefined ? undefined : Number(amount)
    },
  })
}

/**
 * The full price table as a `Map<PricingAction, number>`. Use when a component
 * needs many actions' prices at once (e.g. building the Flow model list).
 */
export function usePricingMapQuery() {
  return useGetPricingQuery(PRICING_QUERY_VARIABLES, {
    ...PRICING_QUERY_OPTIONS,
    select: data => new Map(data.items.map(item => [item.action, Number(item.amount)])),
  })
}
